import React, { useContext, useEffect, useState } from 'react'
import { AppContext } from '@state/context'
import { BUTTON_TYPE, CONTAINER_STYLE, DIVIDER_SIZE } from '@helpers/constants'
import Container from '@components/Container'
import Divider from '@components/Divider'
import Text from '@components/Text'
import ButtonWithLoader from '@components/ButtonWithLoader'
import { useConfirm } from '@hooks/useConfirm'
import { useDeleteCustomer } from '@hooks/mutations/useDeleteCustomer'
import Form from '@components/Form'
import { useForm } from 'react-hook-form'
import BreadCrumbs from '@components/BreadCrumbs'
import { PlusLarge } from '@components/Svg/SVGIcons'
import { storagePath } from '@helpers/variables'
import classNames from 'classnames'
import { useCurrentUser } from '@hooks/useCurrentUser'
import { useUpdateCurrentUser } from '@hooks/mutations/useUpdateCurrentUser'
const punycode = require('punycode/')

const Profile = () => {
  const { currentUser } = useCurrentUser()
  const { id: userId, email: userEmail, image: userImage } = currentUser ?? {}
  const [state] = useContext(AppContext)
  const { confirm } = useConfirm()
  const {
    app: { userName },
  } = state
  const [imagePreviewUrl, setImagePreviewUrl] = useState(
    userImage ? `${storagePath}/${userImage}` : undefined
  )

  const { post: postDelete, isPosting: isPostingDelete } = useDeleteCustomer()
  const { post: postUpdate, isPosting: isPostingUpdate } =
    useUpdateCurrentUser()

  const deleteUser = async () => {
    const confirmed = await confirm({
      title: userName,
      text: 'Är du säker på att du vill radera din användare?',
      confirmText: 'Ja, radera användare',
      cancelText: 'Avbryt',
    })

    if (confirmed) {
      const res = await postDelete({
        id: userId,
      })

      if (res.success) {
        location.href = '/'
      }
    }
  }

  const submitForm = (data) => {
    postUpdate({
      id: parseInt(userId),
      newPassword: data.newPassword,
      currentPassword: data.newPassword ? data.currentPassword : undefined,
      image: data?.image[0],
    })
  }

  const { register, handleSubmit, errors, formState, getValues, reset } = useForm({
    defaultValues: {
      password: '',
      image: '',
    },
  })
  const { isDirty } = formState

  const handleImageChange = (e) => {
    e.preventDefault()
    let reader = new FileReader()
    let file = e.target.files[0]

    reader.onloadend = () => {
      setImagePreviewUrl(reader.result)
    }

    if (file) {
      reader.readAsDataURL(file)
    }
  }

  return (
    <>
      <div
        className={'h-[66px] flex items-center xl:container xl:mx-auto px-10'}
      >
        <BreadCrumbs darkText={true} />
      </div>

      <Container size={CONTAINER_STYLE.DESKTOP} paddingTop={false}>
        <Divider size={DIVIDER_SIZE.SMALL} />

        <div className={'mx-auto max-w-[350px]'}>
          <Form onSubmit={handleSubmit(submitForm)}>
            <>
              <div className='relative flex justify-center'>
                <input
                  type='file'
                  name='image'
                  id='image'
                  ref={register()}
                  src={getValues().image}
                  onChange={handleImageChange}
                  accept='image/png, image/jpeg'
                />
                <Form.Label htmlFor='image'>
                  {userImage || imagePreviewUrl ? (
                    <img
                      src={
                        imagePreviewUrl
                          ? imagePreviewUrl
                          : `${storagePath}/${userImage}`
                      }
                      alt='Profilbild'
                      className={
                        'w-40 h-40 rounded-full object-cover cursor-pointer'
                      }
                    />
                  ) : (
                    <div className={'w-40 h-40'} />
                  )}
                  <div
                    className={classNames(
                      'absolute top-0 flex justify-center items-center transition-opacity w-40 h-40 rounded-full object-cover cursor-pointer hover:opacity-100',
                      userImage || imagePreviewUrl ? 'opacity-0' : 'opacity-100'
                    )}
                  >
                    <div
                      className={
                        'w-full h-full rounded-full cursor-pointer bg-mediumGrey opacity-50'
                      }
                    />
                    <div
                      className={
                        'absolute top-0 h-full flex items-center justify-center cursor-pointer'
                      }
                    >
                      <PlusLarge />
                    </div>
                  </div>
                </Form.Label>
              </div>

              <Divider size={DIVIDER_SIZE.BASE} />

              <span className={'block font-bold'}>Mail</span>
              <Text>{userEmail ? punycode.toUnicode(userEmail) : null}</Text>

              <Divider size={DIVIDER_SIZE.SMALL} />
              <Form.Label htmlFor='currentPassword'>
                Nuvarande lösenord
              </Form.Label>
              <Form.Input
                type={'password'}
                name='currentPassword'
                id='currentPassword'
                ref={register({ required: !!getValues().newPassword })}
                placeholder='Lösenord'
              />
              {errors.currentPassword && (
                <Form.ErrorMessage>
                  Fyll i ditt nuvarande lösenord
                </Form.ErrorMessage>
              )}

              <Divider size={DIVIDER_SIZE.SMALL} />
              <Form.Label htmlFor='newPassword'>Nytt Lösenord</Form.Label>
              <Form.Input
                type={'password'}
                name='newPassword'
                id='newPassword'
                ref={register({ required: false })}
                placeholder='Lösenord'
              />
              {errors.newPassword && (
                <Form.ErrorMessage>Fyll i ditt nya lösenord</Form.ErrorMessage>
              )}

              <Divider size={DIVIDER_SIZE.SMALL} />
              <Form.Label htmlFor='repeatPassword'>Upprepa lösenord</Form.Label>
              <Form.Input
                type={'password'}
                name='repeatPassword'
                id='repeatPassword'
                ref={register({
                  required: !!getValues().newPassword,
                  validate: {
                    passwordsMatch: (value) =>
                      getValues().newPassword === value,
                  },
                })}
                placeholder='Lösenord'
              />
              {errors.repeatPassword && (
                <Form.ErrorMessage>Lösenorden måste stämma</Form.ErrorMessage>
              )}

              <Divider size={DIVIDER_SIZE.BASE} />

              <div className='flex items-center'>
                <ButtonWithLoader
                  type={BUTTON_TYPE.PRIMARY}
                  isLoading={isPostingUpdate}
                  enabledText={'Spara ändringar'}
                  disabled={!isDirty || isPostingUpdate}
                  marginRight
                />
                <ButtonWithLoader
                  type={BUTTON_TYPE.PRIMARY_OUTLINE}
                  isLoading={isPostingDelete}
                  enabledText={'Radera konto'}
                  disabled={isPostingDelete}
                  handleClick={deleteUser}
                  nativeTypeAttribute={'button'}
                  margin
                />
              </div>
            </>
          </Form>

          <Divider size={DIVIDER_SIZE.BASE} />
          
        </div>
      </Container>
    </>
  )
}

export default Profile
